import React, { createContext, useContext, useEffect, useState } from 'react';
import api from '../api';
import { AuthContext } from './AuthContext';

const NotificationContext = createContext();

export function NotificationProvider({ children }){
  const { user, token } = useContext(AuthContext);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (user && token) {
      fetchNotifications();
      fetchUnreadCount();
      // Poll for new notifications every 30 seconds
      const interval = setInterval(() => {
        fetchNotifications();
        fetchUnreadCount();
      }, 30000);
      return () => clearInterval(interval);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token]);

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/api/notifications', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(res.data);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const res = await api.get('/api/notifications/unread-count', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(res.data.count);
    } catch (err) {
      console.error('Failed to fetch unread count:', err);
    }
  };

  async function markRead(id){
    try {
      await api.put(`/api/notifications/${id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(s => s.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  }

  async function removeNotification(id){
    try {
      await api.delete(`/api/notifications/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(s => s.filter(n => n.id !== id));
      fetchUnreadCount();
    } catch (err) {
      console.error('Failed to remove notification:', err);
    }
  }

  function addNotification(text){
    // For local notifications only (optimistic UI updates)
    const item = { 
      id: Date.now(), 
      title: 'Notification',
      message: text || '',
      read: false, 
      created_at: new Date().toISOString() 
    };
    setNotifications(s => [item, ...s]);
    setUnreadCount(prev => prev + 1);
  }

  return (
    <NotificationContext.Provider value={{ notifications, unreadCount, addNotification, markRead, removeNotification }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications(){
  return useContext(NotificationContext);
}

export default NotificationContext;
